
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Elements } from "@stripe/react-stripe-js";
import { loadStripe, Stripe } from "@stripe/stripe-js";
import { StripeCheckoutForm } from "./StripeCheckoutForm";
import { supabase } from "@/lib/supabase";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { useEffect, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";

interface PaymentDialogProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
  amount: number;
  clientSecret: string | null;
  creatorId: string;
  subscriptionType: 'monthly' | 'lifetime';
  username:string;
}

export const PaymentDialog = ({
  isOpen,
  onClose,
  onSuccess,
  amount,
  clientSecret,
  creatorId,
  subscriptionType,
  username
}: PaymentDialogProps) => {
  const [stripePromise, setStripePromise] = useState<Promise<Stripe | null> | null>(null);
  const [publishableKey, setPublishableKey] = useState<string | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const queryClient = useQueryClient();

  useEffect(() => {
    const fetchStripeConfig = async () => {
      setLoading(true);
      setError(null);

      console.log('Fetching Stripe config...');
      
      const { data, error } = await supabase
        .from('stripe_config')
        .select('publishable_key')
        .single();

      if (error) {
        console.error('Error fetching Stripe config:', error);
        toast.error('Failed to load payment configuration');
        setError(error.message);
      } else if (!data) {
        console.error('No configuration data received');
        setError('No configuration data received');
      } else {
        console.log('Successfully retrieved Stripe config:', data);
        setPublishableKey(data.publishable_key);
      }

      setLoading(false);
    };

    fetchStripeConfig();
  }, []);

  useEffect(() => {
    if (publishableKey) {
      console.log("Initializing Stripe with key:", publishableKey);
      setStripePromise(loadStripe(publishableKey));
    }
  }, [publishableKey]);

  const handlePaymentSuccess = async () => {
    await queryClient.invalidateQueries({ queryKey: ["is-subscribed"] });
    onSuccess();
  };

  // Show loading state
  if (loading || !stripePromise) {
    return (
      <Dialog open={isOpen} onOpenChange={onClose}>
        <DialogContent className="sm:max-w-md max-h-[90vh] overflow-y-auto" forceMount>
          <div className="flex items-center justify-center py-8">
            <Loader2 className="h-8 w-8 animate-spin text-primary" />
          </div>
        </DialogContent>
      </Dialog>
    );
  }

  // Show error dialog if configuration fails
  if (error || !publishableKey) {
    return (
      <Dialog open={isOpen} onOpenChange={onClose}>
        <DialogContent className="sm:max-w-md max-h-[90vh] overflow-y-auto" forceMount>
          <div className="flex flex-col items-center justify-center py-8 gap-4">
            <p className="text-red-600">Failed to load payment configuration</p>
            <p className="text-sm text-gray-500">{error || 'Stripe configuration is missing'}</p>
            <Button onClick={onClose} variant="outline">Close</Button>
          </div>
        </DialogContent>
      </Dialog>
    );
  }

  // Show error if missing required data
  if (!clientSecret) {
    console.log("Missing required data:", { hasClientSecret: !!clientSecret, hasStripePromise: !!stripePromise });
    return (
      <Dialog open={isOpen} onOpenChange={onClose}>
        <DialogContent className="sm:max-w-md max-h-[90vh] overflow-y-auto" forceMount>
          <div className="flex flex-col items-center justify-center py-8 gap-4">
            <p className="text-red-600">Payment session not initialized</p>
            <p className="text-sm text-gray-500">Missing client secret</p>
            <Button onClick={onClose} variant="outline">Close</Button>
          </div>
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-md max-h-[90vh] overflow-y-auto" forceMount>
        <DialogHeader>
          <DialogTitle>Complete your purchase</DialogTitle>
        </DialogHeader>
        <div className="mt-4">
          <Elements
            stripe={stripePromise}
            options={{
              clientSecret,
              appearance: {
                theme: 'stripe',
              },
            }}
          >
            <StripeCheckoutForm
              onSuccess={handlePaymentSuccess}
              onCancel={onClose}
              creatorId={creatorId}
              subscriptionType={subscriptionType}
              amount={amount}
              username={username}
            />
          </Elements>
        </div>
      </DialogContent>
    </Dialog>
  );
};

